import ProductListTable from "@iewccouk/Overrides/Widgets/ProductList/ProductListTable";
import { CustomState } from "@iewccouk/Store/Reducers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import Zone from "@insite/client-framework/Components/Zone";
import { getSelectedCategoryPath, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import setTableColumns from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetTableColumns";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import setVisibleColumnNames from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetVisibleColumnNames";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardContainerMultiColumn, {
    CardContainerMultiColumnStyles,
} from "@insite/content-library/Components/CardContainerMultiColumn";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import ProductListProductCard from "@insite/content-library/Widgets/ProductList/ProductListProductCard";
import ProductListProductContext from "@insite/content-library/Widgets/ProductList/ProductListProductContext";
import ProductListProductGridCard from "@insite/content-library/Widgets/ProductList/ProductListProductGridCard";
import Hidden from "@insite/mobius/Hidden";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import sortBy from "lodash/sortBy";
import React, { ReactNode, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    showImage = "showImage",
    showCompare = "showCompare",
    showBrand = "showBrand",
    showTitle = "showTitle",
    showPartNumbers = "showPartNumbers",
    showAvailability = "showAvailability",
    showAttributes = "showAttributes",
    showPrice = "showPrice",
    showAddToList = "showAddToList",
    productListDefaultView = "productListDefaultView",
    showImageBadges = "showImageBadges",
    maximumNumberOfImageBadges = "maximumNumberOfImageBadges",
    showTextBadges = "showTextBadges",
    maximumNumberOfTextBadges = "maximumNumberOfTextBadges",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showImage]: boolean;
        [fields.showCompare]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrand]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showAvailability]: boolean;
        [fields.showAttributes]: boolean;
        [fields.showPrice]: boolean;
        [fields.showAddToList]: boolean;
        [fields.productListDefaultView]: ProductListViewType;
        [fields.showImageBadges]: boolean;
        [fields.maximumNumberOfImageBadges]: number;
        [fields.showTextBadges]: boolean;
        [fields.maximumNumberOfTextBadges]: number;
    };
}

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const {
        pages: {
            productList: {
                isLoading,
                isSearchPage,
                view,
                productFilters: { pageCategoryId },
            },
        },
    } = state;

    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);
    const productListCatalogPage= getCatalogPageStateByPath(state, path).value;

    let customView = view;

    if (
        productListCatalogPage &&
        !isSearchPage &&
        (productListCatalogPage.properties?.productAttributes?.length > 0 ||
            productListCatalogPage.properties?.productProperties?.length > 0)
    ) {
        customView = "Table";
    } else {
        customView = "List";
    }

    return {
        isLoading,
        productsDataView: getProductListDataView(state),
        view: ownProps.fields.productListDefaultView, // Verndale customization - We are defining it on the CMS
        isSearchPage,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
        includeRealTimeInventoryAndPricingForSeo:
            getSettingsCollection(state).websiteSettings.includeRealTimeInventoryAndPricingForSeo,
        pageCategoryId,
        // Verndale customization
        isTableView: customView === "Table",
    };
};

const mapDispatchToProps = {
    setView,
    setTableColumns,
    setVisibleColumnNames,
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListCardListStyles {
    wrapper?: InjectableCss;
    cardList?: CardListStyles;
    noProductsText?: TypographyPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    cardContainerStyles?: CardContainerStyles;
    cardContainerMultiColumnStyles?: CardContainerMultiColumnStyles;
}

export const listStyles: ProductListCardListStyles = {
    wrapper: {
        css: css`
            margin-top: 20px;
            border-top: 1px solid ${getColor("common.border")};
        `,
    },
    cardList: {
        gridContainer: {
            gap: 0,
        },
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noProductsText: {
        variant: "h4",
        css: css`
            display: block;
            margin: auto;
        `,
    },
    cardContainerStyles: {
        cardDivider: {
            css: css`
                width: 100%;
            `,
        },
        gridItem: {
            css: css`
                border-bottom: 1px solid ${getColor("common.border")};
            `,
        },
    },
};

const styles = listStyles;

const ProductListCardList = ({
    id,
    isLoading,
    isSearchPage,
    productsDataView,
    view,
    fields,
    attributeTypeFacets,
    brandFacets,
    productLineFacets,
    includeRealTimeInventoryAndPricingForSeo,
    pageCategoryId,
    setView,
    setTableColumns,
    setVisibleColumnNames,
    // Verndale customization
    isTableView,
}: Props) => {
    useEffect(() => {
        setView({ view });
    }, []);

    useEffect(() => {
        if (!attributeTypeFacets?.length && !brandFacets?.length && !productLineFacets?.length) {
            setTableColumns({ tableColumns: [] });
            setVisibleColumnNames({ visibleColumnNames: [] });
            return;
        }

        const columns = attributeTypeFacets
            ? sortBy(
                  attributeTypeFacets,
                  o => o.sortOrder,
                  o => o.name,
              )
            : [];
        if (brandFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Brand",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        if (productLineFacets?.length) {
            columns.push({
                attributeTypeId: "",
                name: "Product Line",
                nameDisplay: "",
                sortOrder: 0,
                attributeValueFacets: [],
            });
        }

        setTableColumns({ tableColumns: columns });
        setVisibleColumnNames({ visibleColumnNames: columns?.slice(0, 3).map(o => o.name) || [] });
    }, [pageCategoryId, attributeTypeFacets?.length, brandFacets?.length, productLineFacets?.length]);

    // Verndale Customization - We dont want to render empty products for table view
    if (isLoading && !productsDataView.value && isTableView) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    if (!productsDataView.value) {
        return null;
    }

    const products = productsDataView.value;
    let totalItemCount = 0;
    if (productsDataView && productsDataView.value && productsDataView.pagination?.totalItemCount) {
        totalItemCount = productsDataView.pagination.totalItemCount;
    }

    if (products && products.length === 0) {
        return totalItemCount === 0 ? (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.centeringWrapper} data-test-selector="productListNoneFound">
                    {isSearchPage && <Zone contentId={id} zoneName="Content00" />}
                    {!isSearchPage && <Zone contentId={id} zoneName="Content01" />}
                </StyledWrapper>
            </StyledWrapper>
        ) : (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
            </StyledWrapper>
        );
    }

    const renderProducts = (children: ReactNode) => {
        return products?.map((product, idx) => (
            <ProductListProductContext product={product} idx={idx} key={product.id}>
                {children}
            </ProductListProductContext>
        ));
    };

    const overriddenProps = { ...fields };
    if (getIsWebCrawler() && !includeRealTimeInventoryAndPricingForSeo) {
        overriddenProps.showAvailability = false;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductListPageDataContext.Consumer>
                {({ ref }) => {
                    return ref ? <span ref={ref} tabIndex={-1} /> : undefined;
                }}
            </ProductListPageDataContext.Consumer>
            <LoadingOverlay loading={isLoading && !isTableView}>
                <Hidden below="md">
                    <CardList extendedStyles={styles.cardList} data-test-selector={`productListCards_${view}`}>
                        {/* Verndale Customization - We dont pass props products to the custom table  */}
                        {isTableView ? (
                            <ProductListTable {...overriddenProps} />
                        ) : (
                            renderProducts(
                                view === "List" ? (
                                    <CardContainer extendedStyles={styles.cardContainerStyles}>
                                        <ProductListProductCard {...overriddenProps} />
                                    </CardContainer>
                                ) : (
                                    <CardContainerMultiColumn extendedStyles={styles.cardContainerMultiColumnStyles}>
                                        <ProductListProductGridCard {...overriddenProps} />
                                    </CardContainerMultiColumn>
                                ),
                            )
                        )}
                    </CardList>
                </Hidden>
                <Hidden above="sm">
                    <CardList extendedStyles={styles.cardList} data-test-selector="cardListProductsNarrow">
                        {renderProducts(
                            <CardContainer extendedStyles={styles.cardContainerStyles}>
                                <ProductListProductCard {...overriddenProps} />
                            </CardContainer>,
                        )}
                    </CardList>
                </Hidden>
            </LoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListCardList as any) as any,
    definition: {
        group: "Product List",
        displayName: "Card List",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.showImage,
                displayName: "Show Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showCompare,
                displayName: "Show Compare",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showBrand,
                displayName: "Show Brand",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showTitle,
                displayName: "Show Title",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showPartNumbers,
                displayName: "Show Part Numbers",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showAvailability,
                displayName: "Show Availability",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showAttributes,
                displayName: "Show Attributes",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showPrice,
                displayName: "Show Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToList,
                displayName: "Show Add To List",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.productListDefaultView,
                editorTemplate: "RadioButtonsField",
                defaultValue: "List",
                fieldType: "General",
                sortOrder: 10,
                options: [
                    {
                        displayName: "List",
                        value: "List",
                    },
                    {
                        displayName: "Grid",
                        value: "Grid",
                    },
                    {
                        displayName: "Table",
                        value: "Table",
                    },
                ],
            },
            {
                name: fields.showImageBadges,
                displayName: "Show Image Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.maximumNumberOfImageBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showImageBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 12,
            },
            {
                name: fields.showTextBadges,
                displayName: "Show Text Badges",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 13,
            },
            {
                name: fields.maximumNumberOfTextBadges,
                editorTemplate: "DropDownField",
                isVisible: widget => widget.fields.showTextBadges,
                defaultValue: "4",
                options: [
                    { displayName: "1", value: "1" },
                    { displayName: "2", value: "2" },
                    { displayName: "3", value: "3" },
                    { displayName: "4", value: "4" },
                    { displayName: "5", value: "5" },
                    { displayName: "6", value: "6" },
                    { displayName: "7", value: "7" },
                    { displayName: "8", value: "8" },
                ],
                fieldType: "General",
                sortOrder: 14,
            },
        ],
    },
};

export default widgetModule;
